import Service, { inject as service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { isPresent } from '@ember/utils';

import { DiscoveryListTask } from 'later/utils/constants';
import { SegmentEventTypes } from 'later/utils/constants/segment-events';
import { timestamp } from 'later/utils/time-format';

import type { UserAndAccountTraits } from './chatbot-wrapper';
import type AlertsService from '../alerts';
import type AuthService from '../auth';
import type IntegrationsQueueService from '../integrations-queue';
import type SegmentService from '../segment';
import type SubscriptionsService from '../subscriptions';
import type StoreService from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type { UntypedService } from 'shared/types';

export interface ChatbotService {
  readonly name: string;
  show: () => void;
  hide: () => void;
  setup: (userAndAccountTraits: UserAndAccountTraits | undefined) => Promise<void>;
  open: () => void;
  close: () => void;
  reset: () => void;
}

export default abstract class BaseChatbotService extends Service implements ChatbotService {
  @service declare alerts: AlertsService;
  @service declare auth: AuthService;
  // eslint-disable-next-line ember/no-unused-services
  @service declare errors: UntypedService;
  @service declare integrationsQueue: IntegrationsQueueService;
  @service declare intl: IntlService;
  @service declare segment: SegmentService;
  @service declare store: StoreService;
  @service declare subscriptions: SubscriptionsService;

  abstract readonly name: string;
  abstract show(): void;
  abstract hide(): void;
  abstract setup(userAndAccountTraits: UserAndAccountTraits | undefined): Promise<void>;
  abstract open(): void;
  abstract close(): void;
  abstract reset(): void;

  onOpen(): void {
    this.integrationsQueue.addToQueue('chatbot', true);
    this.trackOpened();
  }

  onClose(): void {
    this.integrationsQueue.removeFromQueue('chatbot');
  }

  trackOpened(): void {
    const { hasActiveTrial } = this.subscriptions;
    this.segment.track(SegmentEventTypes.ChatbotOpened, { active_trial: !!hasActiveTrial });

    const hasOpenedChatbotPreviously = isPresent(
      this.store.peekAll('discovery-list-task').find(({ identifier }) => identifier === DiscoveryListTask.OpenedChatbot)
    );

    if (!hasOpenedChatbotPreviously) {
      const item = this.store.createRecord('discovery-list-task', {
        identifier: DiscoveryListTask.OpenedChatbot,
        completedTime: timestamp(),
        user: this.auth.currentUserModel
      });
      item.save();
    }
  }

  displayError(): void {
    this.alerts.warning(htmlSafe(this.intl.t('alerts.customer_support.error.service_not_available.message')), {
      title: this.intl.t('alerts.customer_support.error.service_not_available.title')
    });
  }
}
